import type { UsageInfo } from './anthropic';

export const DEFAULT_MODEL = 'claude-sonnet-5';
/** Cheap/fast model used for background tasks like D1's LLM-assisted history summarization — never the main chat model. */
export const SUMMARIZATION_MODEL = 'claude-haiku-4-5';
const SONNET5_INTRO_UNTIL = Date.parse('2026-08-31T00:00:00Z');

/** Each current model's real context window, in tokens — used by the Context Health indicator. */
export const CONTEXT_WINDOW_TOKENS: Record<string, number> = {
  'claude-sonnet-5': 1_000_000,
  'claude-opus-4-8': 1_000_000,
  'claude-haiku-4-5': 200_000,
};

/** Models offered in the chat pane's model switcher. */
export const AVAILABLE_MODELS: { id: string; label: string }[] = [
  { id: 'claude-opus-4-8', label: 'Opus 4.8 — most capable' },
  { id: 'claude-sonnet-5', label: 'Sonnet 5 — balanced (default)' },
  { id: 'claude-haiku-4-5', label: 'Haiku 4.5 — fastest & cheapest' },
];

export function contextWindowFor(model: string): number {
  return CONTEXT_WINDOW_TOKENS[model] ?? 200_000;
}

function pricingFor(model: string): { input: number; output: number } {
  switch (model) {
    case 'claude-opus-4-8':
      return { input: 5, output: 25 };
    case 'claude-sonnet-5':
      return Date.now() < SONNET5_INTRO_UNTIL ? { input: 2, output: 10 } : { input: 3, output: 15 };
    case 'claude-haiku-4-5':
      return { input: 1, output: 5 };
    default:
      return { input: 3, output: 15 };
  }
}

// Standard ephemeral-cache economics: writes ~1.25x base input, reads ~0.1x base input.
export function costForUsage(usage: UsageInfo, model: string): number {
  const p = pricingFor(model);
  const perM = (tokens: number, rate: number) => (tokens / 1_000_000) * rate;
  return (
    perM(usage.input_tokens ?? 0, p.input) +
    perM(usage.output_tokens ?? 0, p.output) +
    perM(usage.cache_creation_input_tokens ?? 0, p.input * 1.25) +
    perM(usage.cache_read_input_tokens ?? 0, p.input * 0.1)
  );
}
